/**
 * AuthLayout — Anmeldung im Basis-Stil (Board „Anmeldung A · Geteilt“, PO 23.10.): links das Foto
 * eines StayOn-Hauses unter einem Waldverlauf mit Zeichen und Begrüßung, rechts die Fläche mit dem
 * Formular und klein darunter Impressum und Datenschutz. Schmaler als 900 px steht nur das Zeichen
 * über dem Formular, damit es ohne Scrollen beginnt.
 */
import type { ReactNode } from 'react';
import { AuthLegalFooter } from '@/features/auth/AuthLegalFooter';
import { ShellLogo as BrandMark } from './NavEntryLink';

export interface AuthLayoutProps {
  children: ReactNode;
  /** `new` für die Aktivierung: wer eingeladen wurde, war noch nie da. */
  greeting?: 'back' | 'new';
}

const GREETING = {
  back: 'Schön, dass du wieder da bist.',
  new: 'Willkommen bei StayOn.',
};

export function AuthLayout({ children, greeting = 'back' }: AuthLayoutProps) {
  return (
    <div className="bg-frame text-ink @container min-h-dvh sm:p-3">
      <div className="@[900px]:grid-cols-[5fr_7fr] grid min-h-dvh gap-3 sm:min-h-[calc(100dvh-1.5rem)]">
        <aside
          aria-label="StayOn"
          className="on-forest bg-forest-deep rounded-hero @[900px]:flex relative hidden flex-col justify-between overflow-hidden p-10 text-white"
        >
          <img
            src="/login-stayon-haus.jpg"
            alt=""
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover object-[70%_center]"
          />
          {/* Wald über dem Foto: oben und unten dicht, damit Zeichen und Text lesbar bleiben. */}
          <span
            aria-hidden
            className="from-forest-deep/90 via-forest-deep/25 to-forest-deep/95 absolute inset-0 bg-gradient-to-b"
          />
          <span className="relative flex items-center gap-3.5 font-serif text-[26px] font-bold">
            <BrandMark size={48} /> StayOn
          </span>
          <div className="relative flex max-w-md flex-col gap-4">
            <span className="text-mint font-mono text-[11px] font-semibold uppercase tracking-[0.16em]">
              Für dein Team
            </span>
            <span className="font-serif text-[52px] font-bold leading-none tracking-[-0.035em]">
              {GREETING[greeting]}
            </span>
            <span className="text-on-forest-soft text-base leading-relaxed">
              Buchungen, Kalender, Nachrichten und Reinigung an einem Ort. Nach der Anmeldung
              landest du dort, wo deine Rolle arbeitet.
            </span>
          </div>
        </aside>
        <main className="bg-paper sm:rounded-frame flex flex-col px-4 py-8 sm:p-10">
          <div className="animate-page-enter mx-auto flex w-full max-w-[440px] flex-1 flex-col justify-center gap-6">
            <span className="@[900px]:hidden flex items-center gap-3 font-serif text-xl font-bold">
              <BrandMark size={36} /> StayOn
            </span>
            {children}
          </div>
          <AuthLegalFooter />
        </main>
      </div>
    </div>
  );
}
